import { Card, Input } from "@ndd/ui";
import React, { useState } from "react";
import type { ScorecardWorkbenchSessionSummary } from "../../../../shared/scorecard-workbench";
import { Badge } from "../badge";

/** The Session date is a calendar day, so the viewer's timezone must not shift it. */
const DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

function shortDate(isoDate: string): string {
  const parsed = Date.parse(`${isoDate}T00:00:00Z`);

  return Number.isFinite(parsed) ? DATE.format(new Date(parsed)) : isoDate;
}

function RecentRow({
  session,
  onSelect,
}: {
  session: ScorecardWorkbenchSessionSummary;
  onSelect: (session: ScorecardWorkbenchSessionSummary) => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onSelect(session)}
      className="flex w-full items-center gap-3 border-b border-sand-100 px-3 py-2 text-left last:border-b-0 hover:bg-muted"
    >
      <span
        title={session.sessionId}
        className="w-24 shrink-0 truncate font-mono text-body text-muted-foreground"
      >
        {session.sessionId}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">
        <span className="font-semibold text-foreground">{session.clientName}</span>
        <span className="text-muted-foreground">
          {" · "}
          {session.rdName}
          {" · "}
          {session.sessionType === "initial" ? "Initial" : "Follow-up"}
        </span>
      </span>
      {session.baselineReady ? null : <Badge variant="analyzing">Analyzing</Badge>}
      <span className="shrink-0 text-body text-muted-foreground">
        {shortDate(session.sessionDate)}
      </span>
    </button>
  );
}

/**
 * The list names clients, so it lives on screen only: nothing here is written
 * to the URL, storage, or telemetry, and every pick is a Clinical Read.
 */
export function SessionPicker({
  sessions,
  listFailed,
  onRetry,
  onSelect,
  onSubmitId,
}: {
  sessions: readonly ScorecardWorkbenchSessionSummary[];
  listFailed: boolean;
  onRetry: () => void;
  onSelect: (session: ScorecardWorkbenchSessionSummary) => void;
  onSubmitId: (sessionId: string) => void;
}): React.ReactElement {
  const [typedId, setTypedId] = useState("");

  return (
    <Card className="max-w-[640px] p-4">
      <h3 className="text-sm font-semibold text-foreground">Clinical Session</h3>
      <form
        className="mt-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = typedId.trim();
          if (trimmed) onSubmitId(trimmed);
        }}
      >
        <label className="sr-only" htmlFor="scorecard-workbench-session-id">
          Session id
        </label>
        <Input
          id="scorecard-workbench-session-id"
          value={typedId}
          placeholder="Paste a Session id..."
          autoComplete="off"
          onChange={(event) => setTypedId(event.target.value)}
        />
      </form>
      <h4 className="mt-4 text-body text-muted-foreground">Recently completed</h4>
      {listFailed ? (
        <p className="mt-2 rounded-md border border-border px-3 py-3 text-body text-muted-foreground">
          The recent Sessions could not be loaded.{" "}
          <button type="button" onClick={onRetry} className="underline">
            Try again
          </button>
          , or paste a Session id above.
        </p>
      ) : (
        <div className="mt-2 rounded-md border border-border">
          {sessions.map((session) => (
            <RecentRow key={session.sessionId} session={session} onSelect={onSelect} />
          ))}
        </div>
      )}
      <p className="mt-3 text-body text-muted-foreground">
        Each pick is a Clinical Read of that Session.
      </p>
    </Card>
  );
}
